import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import "./card.css";
import { TextCss } from "../../components/GlobalCss/TextCss";

export function Accordion({ children, className = "" }) {
  return <div className={`custom-accordion ${className}`}>{children}</div>;
}

export function AccordionSection({ title, children, isOpen = false, isDisabled = false, onToggle }) {
  const contentRef = useRef(null);
  const contentInnerRef = useRef(null);

  // State to handle the transition phase regarding overflow
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Opening Logic
      if (contentRef.current && contentInnerRef.current) {
        const contentHeight = contentInnerRef.current.scrollHeight;
        const extraSpace = 20; // Padding buffer

        // 1. Set explicit height for animation to start
        contentRef.current.style.height = `${contentHeight + extraSpace}px`;

        // Reset animation complete status
        setIsAnimationComplete(false);
      }
    } else {
      // Closing Logic
      if (contentRef.current && contentInnerRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        contentRef.current.style.height = `${contentHeight}px`;
        contentRef.current.offsetHeight;
        contentRef.current.style.height = '0';

        setIsAnimationComplete(false);
      }
    }
  }, [isOpen, children]); // Trigger on Open/Close or if children change completely

  // This handles the end of the CSS transition
  const handleTransitionEnd = () => {
    if (isOpen && contentRef.current) {
      // Animation finished opening:
      // Set height to auto so content can resize naturally
      contentRef.current.style.height = 'auto';
      // Mark animation as complete to enable visible overflow
      setIsAnimationComplete(true);
    }
  };

  const handleToggle = useCallback(() => {
    if (!isDisabled) {
      onToggle();
    }
  }, [isDisabled, onToggle]);

  return (
    <div className={`accordion-section ${isOpen ? "open" : ""}`}>
      <button
        type="button"
        disabled={isDisabled}
        className="accordion-header"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <p className={isDisabled ? TextCss.grey_neutral.sub_heading_600_14 : TextCss.black.sub_heading_600_14}>{title}</p>
        <span className="accordion-icon ">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>

      <div
        className="accordion-content-wrapper"
        ref={contentRef}
        onTransitionEnd={handleTransitionEnd}
        style={{
          // Key change: If open and animation done, show overflow. Otherwise hide it for animation.
          overflow: isOpen && isAnimationComplete ? 'visible' : 'hidden',
          transition: 'height 0.3s ease-out',
        }}
      >
        <div className="accordion-content" ref={contentInnerRef}>
          {children}
        </div>
      </div>
    </div>
  );
}